import { useCallback, useEffect, useState } from 'react';

const API = '/api';

// ─── useNotifications ──────────────────────────────────────────────
// Encapsulates the header notification bell state:
//   • poll `/notifications/unread-count` every 30s while a user is
//     logged in
//   • lazily load the notification list when the dropdown is opened
//   • mark everything as read on open (server-side + local zeroing)
//
// Returns { unreadCount, notifications, notifOpen, handleOpen } so the
// bell UI can stay dumb.
export function useNotifications(user) {
    const [unreadCount, setUnreadCount] = useState(0);
    const [notifications, setNotifications] = useState([]);
    const [notifOpen, setNotifOpen] = useState(false);

    const fetchUnreadCount = useCallback(() => {
        if (!user) return;
        fetch(`${API}/notifications/unread-count`, { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : { count: 0 }))
            .then((d) => setUnreadCount(d.count))
            .catch((e) => console.warn('Notification count fetch failed:', e));
    }, [user]);

    const fetchNotifications = useCallback(() => {
        if (!user) return;
        fetch(`${API}/notifications?limit=20`, { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : []))
            .then((items) => setNotifications(items))
            .catch((e) => console.warn('Notifications fetch failed:', e));
    }, [user]);

    useEffect(() => {
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 30000);
        return () => clearInterval(interval);
    }, [fetchUnreadCount]);

    const handleOpen = () => {
        const willOpen = !notifOpen;
        setNotifOpen(willOpen);
        if (willOpen) {
            fetchNotifications();
            fetch(`${API}/notifications/read-all`, { method: 'POST', credentials: 'include' })
                .then(() => setUnreadCount(0))
                .catch((e) => console.warn('Mark notifications read failed:', e));
        }
    };

    const close = () => setNotifOpen(false);

    return { unreadCount, notifications, notifOpen, handleOpen, close };
}
